import { cn } from '@/shared/utils/cn';

/**
 * A row of exclusive choices with counts: «Todos 12 · Pendientes 3 · Errores 1».
 *
 * Used for the status filters, where a `<select>` hid the one thing worth seeing at a
 * glance (how many are in error). It is a radiogroup for the keyboard.
 *
 * On a phone it does not wrap: the group scrolls sideways on its own, so four options
 * stay one row and the list under them starts at the same height whatever the widths.
 */
export interface SegmentOption<T extends string> {
  value: T;
  label: string;
  count?: number | undefined;
  tone?: 'neutral' | 'ok' | 'warn' | 'danger';
}

export function Segmented<T extends string>({
  label,
  value,
  options,
  onChange,
  className,
}: {
  label: string;
  value: T;
  options: SegmentOption<T>[];
  onChange: (value: T) => void;
  className?: string;
}) {
  const dots = {
    neutral: 'bg-ink-4',
    ok: 'bg-ok',
    warn: 'bg-warn',
    danger: 'bg-danger',
  };
  return (
    <div
      role="radiogroup"
      aria-label={label}
      className={cn(
        'bg-paper-2 border-line inline-flex max-w-full gap-1 overflow-x-auto rounded-lg border p-1',
        className,
      )}
    >
      {options.map((option) => {
        const active = option.value === value;
        return (
          <button
            key={option.value}
            type="button"
            role="radio"
            aria-checked={active}
            onClick={() => onChange(option.value)}
            className={cn(
              'inline-flex h-8 shrink-0 items-center gap-1.5 rounded-md px-2.5 text-[13px] font-medium whitespace-nowrap transition-colors',
              'focus-visible:outline-accent focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-1',
              active ? 'bg-card text-ink shadow-card' : 'text-ink-3 hover:text-ink',
            )}
          >
            {option.tone ? (
              <span aria-hidden="true" className={cn('size-1.5 rounded-full', dots[option.tone])} />
            ) : null}
            {option.label}
            {option.count !== undefined ? (
              <span
                className={cn(
                  'rounded-full px-1.5 text-[11.5px] tabular-nums',
                  active ? 'bg-paper-2 text-ink-2' : 'bg-card/60 text-ink-3',
                )}
              >
                {option.count}
              </span>
            ) : null}
          </button>
        );
      })}
    </div>
  );
}
